import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";

const bunoColors = {
    spaceBlue: '#102A41',       // Primary dark blue
    aquaMist: '#D3E3F2',       // Light blue background
    eggshell: '#EFF0DE',       // Light neutral background
    caramel: '#9C8266',        // Accent brown
    harvestWheat: '#EDDABA',   // Accent beige
    text: '#102A41',           // Use Space Blue for main text
    sandStorm: '#5E5C59',      // Gray
}

const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,      // Mobile
            sm: 600,    // Tablet
            md: 900,    // Small desktop
            lg: 1200,   // Large desktop
            xl: 1536,   // Extra large
        },
    },
    palette: {
        bunoColors: {
            spaceBlue: bunoColors.spaceBlue,
            aquaMist: bunoColors.aquaMist,
            eggshell: bunoColors.eggshell,
            caramel: bunoColors.caramel,
            harvestWheat: bunoColors.harvestWheat,
            sandStorm: bunoColors.sandStorm,
            text: bunoColors.text,
            gray: '#CFCECB',
        },
    },
    typography: {
        fontFamily: 'OCHighwayVAR2Body',
        h1: {
            fontFamily: 'OCHighwayVAR2Heading',
            fontWeight: 700,
            fontSize: '2rem', // Mobile
            lineHeight: 1.13,
            color: bunoColors.spaceBlue,
            '@media (min-width:600px)': { // Tablet
                fontSize: '2.5rem',
            },
            '@media (min-width:900px)': { // Desktop
                fontSize: '3rem',
            },
        },
        h2: {
            fontFamily: 'OCHighwayVAR2Heading',
            fontWeight: 700,
            fontSize: '1.65rem', // Mobile
            lineHeight: 1.15,
            color: bunoColors.spaceBlue,
            '@media (min-width:600px)': { // Tablet
                fontSize: '2rem',
            },
            '@media (min-width:900px)': { // Desktop
                fontSize: '2.5rem',
            },
        },
        h3: {
            fontFamily: 'OCHighwayVAR2Heading',
            fontWeight: 700,
            fontSize: '1.35rem', // Mobile
            lineHeight: 1.18,
            color: bunoColors.spaceBlue,
            '@media (min-width:600px)': { // Tablet
                fontSize: '1.6rem',
            },
            '@media (min-width:900px)': { // Desktop
                fontSize: '1.8rem',
            },
        },
        h4: {
            fontFamily: 'OCHighwayVAR2Heading',
            fontWeight: 700,
            fontSize: '0.472rem', // 0.764rem / 1.618
            lineHeight: 1.23,
            color: bunoColors.spaceBlue,
        },
        h5: {
            fontFamily: 'OCHighwayVAR2Heading',
            fontWeight: 700,
            fontSize: '0.292rem', // 0.472rem / 1.618
            lineHeight: 1.3,
            color: bunoColors.spaceBlue,
        },
        h6: {
            fontFamily: 'OCHighwayVAR2Heading',
            fontWeight: 700,
            fontSize: '0.181rem', // 0.292rem / 1.618
            lineHeight: 1.4,
            color: bunoColors.spaceBlue,
        },
        body1: {
            fontFamily: 'OCHighwayVAR2Body',
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.3,
            color: bunoColors.text,
        },
        body2: {
            fontFamily: 'OCHighwayVAR2Body',
            fontWeight: 400,
            fontSize: '0.95rem',
            lineHeight: 1.6,
            color: bunoColors.text,
        },
        subtitle1: {
            fontFamily: 'OCHighwayVAR2Body',
            fontWeight: 600,
            fontSize: '1.05rem',
            color: bunoColors.spaceBlue,
        },
        subtitle2: {
            fontFamily: 'OCHighwayVAR2Body',
            fontWeight: 600,
            fontSize: '0.95rem',
            color: bunoColors.spaceBlue,
        },
        button: {
            fontFamily: 'OCHighwayVAR2Body',
            fontWeight: 700,
            fontSize: '1.1rem',
        },
        // For script/display text
        caveat: {
            fontFamily: 'Caveat',
            fontWeight: 700,
            fontSize: '1.6rem',
            color: bunoColors.accent,
        }
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiButton: {
        styleOverrides: {
            root: {
                borderRadius: 12,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                padding: '12px 24px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                },
            },
            contained: {
                borderRadius: 30,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                },
            },
        },
        },
    },
});

const CardSection = styled(Box)(({ theme }) => ({
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
    padding: theme.spacing(3, 2),
    margin: theme.spacing(0, 0, 0, 0),
    
    // Responsive padding
    [theme.breakpoints.up('sm')]: {       // Tablet (600px+)
        padding: theme.spacing(4, 3),
    },
    [theme.breakpoints.up('md')]: {       // Desktop (900px+)
        padding: theme.spacing(5, 4),
    },
}));

const Section = styled(Box)(({ theme, backgroundColor }) => ({
    width: '100%',
    backgroundColor: backgroundColor,
    padding: theme.spacing(2),
    borderRadius: '20px',
    margin: theme.spacing(0.5,0),
    boxSizing: 'border-box',
    
    // Responsive max widths and padding
    maxWidth: '600px',                    // Mobile
    [theme.breakpoints.up('sm')]: {       // Tablet (600px+)
        maxWidth: '700px',
        padding: theme.spacing(3),
    },
    [theme.breakpoints.up('md')]: {       // Small desktop (900px+)
        maxWidth: '800px',
        padding: theme.spacing(4),
    },
}));

const Container = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    maxWidth: '100%',
}));

export { theme, CardSection, Section, Container };
